import React from 'react';
import {
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import ProgressBar from '../../../../Shared/ProgressBar';
import { ReactComponent as PatientIcon } from '../../../../../images/icons/assign_user.svg';
import {
  active,
  backgroundActive,
  backgroundInactive,
  inactive,
  lightBlack,
  lightGrey,
  lightTeal,
} from '../../../../../mainTheme';

const COLORS = {
  active: active,
  inactive: inactive,
  text: {
    primary: lightBlack,
    tertiary: lightGrey,
  },
  background: {
    active: backgroundActive,
    inactive: backgroundInactive,
  },
} as const;

const RadioOption: React.FC<{
  value: string;
  title: string;
  isSelected: boolean;
}> = ({ value, title, isSelected }) => (
  <Box
    sx={{
      border: '1px solid',
      borderRadius: '8px',
      borderColor: isSelected ? COLORS.active : COLORS.inactive,
      p: 1,
      mb: 1,
      backgroundColor: isSelected
        ? COLORS.background.active
        : COLORS.background.inactive,
      height: '48px',
      alignItems: 'center',
      display: 'flex',
    }}
  >
    <FormControlLabel
      value={value}
      sx={{
        margin: 0,
        width: '100%',
        justifyContent: 'space-between',
        '& .MuiFormControlLabel-label': { flex: 1 },
      }}
      control={
        <Radio
          sx={{
            '&.MuiRadio-root': {
              color: isSelected ? lightTeal : COLORS.text.tertiary,
            },
          }}
        />
      }
      labelPlacement="start"
      label={
        <Box>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.2px',
              ml: '8px',
              color: lightBlack,
            }}
          >
            {title}
          </Typography>
        </Box>
      }
    />
  </Box>
);

export const PatientTypeSelection = ({
  onSelect,
  onBack,
}: {
  onSelect: (type: 'facility' | 'independent') => void;
  onBack: () => void;
}) => {
  const [selectedType, setSelectedType] = React.useState<
    'facility' | 'independent'
  >('facility');

  const radioOptions = [
    {
      value: 'facility',
      title: 'Yes',
    },
    {
      value: 'independent',
      title: 'No',
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <ProgressBar currentStep={1} totalSteps={2} />
      <PatientIcon />
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '0.2px',
          color: lightBlack,
        }}
      >
        Is the patient associated with a facility?
      </Typography>

      <RadioGroup
        value={selectedType}
        onChange={(e) =>
          setSelectedType(e.target.value as 'facility' | 'independent')
        }
      >
        {radioOptions.map((option) => (
          <RadioOption
            key={option.value}
            {...option}
            isSelected={selectedType === option.value}
          />
        ))}
      </RadioGroup>

      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', mt: '16px' }}
      >
        <Button
          variant="text"
          color="secondary"
          onClick={onBack}
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
            width: '180px',
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => onSelect(selectedType)}
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
            width: '180px',
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};
